'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { Swiper as SwiperCore } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import NewsCard from '../blocks/NewsCard';

interface NewsSlide {
    text: string;
    title: string;
    link: string;
    imageSrc: string;
}

interface PartnerSlide {
    logoSrc: string;
    url: string;
}

type Slide = NewsSlide | PartnerSlide;

interface CarouselProps {
    slides: Slide[];
    slideClasses?: string;
    autoplay?: boolean;
    infinite?: boolean;
    mode?: 'news' | 'partners';
    // eslint-disable-next-line no-unused-vars
    onActiveIndexChange?: (index: number) => void;
}

const isNewsSlide = (slide: Slide): slide is NewsSlide => {
    return (slide as NewsSlide).text !== undefined;
};

const isPartnerSlide = (slide: Slide): slide is PartnerSlide => {
    return (slide as PartnerSlide).logoSrc !== undefined;
};

const Carousel = ({
    slides,
    slideClasses,
    mode = 'news',
    autoplay = false,
    infinite = false,
    onActiveIndexChange,
}: CarouselProps) => {
    const swiperRef = useRef<SwiperCore | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const prevButtonRef = useRef<HTMLDivElement>(null);
    const nextButtonRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current && prevButtonRef.current && nextButtonRef.current) {
            containerRef.current.appendChild(prevButtonRef.current);
            containerRef.current.appendChild(nextButtonRef.current);
        }
    }, []);

    return (
        <div className="relative" ref={containerRef}>
            <Swiper
                slidesPerView={2}
                onSwiper={(swiperInstance) => {
                    swiperRef.current = swiperInstance;
                }}
                autoplay={autoplay}
                loop={infinite}
                modules={[Autoplay]}
                onRealIndexChange={(swiper) => {
                    if (onActiveIndexChange) onActiveIndexChange(swiper.realIndex);
                }}
                spaceBetween={50}
            >
                {slides.map((slide, index) => (
                    <SwiperSlide key={index} className={slideClasses}>
                        {mode === 'news' ? (
                            isNewsSlide(slide) ? (
                                <NewsCard
                                    text={slide.text}
                                    title={slide.title}
                                    link={slide.link}
                                    imageSrc={slide.imageSrc}
                                />
                            ) : null
                        ) : isPartnerSlide(slide) ? (
                            <div className="w-full max-lg:h-36 h-36">
                                <Link href={slide.url}>
                                    <Image
                                        className="object-contain lg:object-left object-center"
                                        src={slide.logoSrc}
                                        alt="Logo"
                                        fill
                                    />
                                </Link>
                            </div>
                        ) : null}
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

export default Carousel;
